import axios from 'axios'
import type {AxiosProgressEvent, AxiosRequestConfig, AxiosResponse} from 'axios'
import {useState} from 'nuxt/app'

const axiosInstance = axios.create({
    baseURL: process.client ? '/coreApi' : 'https://api.craft.mcpanl.cn',
})


interface ObjectLiteral {
    [key: string]: any;
}

type QueryParamsType = {
    [key: string]:
        | string
        | number
        | boolean
        | string[]
        | number[]
        | boolean[]
        | Date
        | Date[]
        | Array<unknown>
        | Number
        | object
        | undefined
        | unknown
};

type ResponseData = {
    code: number
    msg: string
    data: ObjectLiteral
}

const objectToGetParams = (url: string, params?: QueryParamsType): string => {
    const paramStrings: string[] = []
    if (params) {
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const value = params[key]
                if (value !== undefined && value !== null) {
                    let valueStr = ''
                    if (Array.isArray(value)) {
                        valueStr = value.join(',')
                    } else {
                        valueStr = String(value || '')
                    }
                    paramStrings.push(`${key}=${valueStr}`)
                } else {
                    paramStrings.push(key)
                }
            }
        }
    }

    const fullUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + paramStrings.join('&')
    return fullUrl
}

const configRequest = async (config?: AxiosRequestConfig) => {
    const accessToken = process.client ? useState('accessToken') : ref(null)

    if (config) {
        if (config.headers) {
            config.headers['Access-Token'] = accessToken.value ? String(accessToken.value) : null
        }
        return config
    } else {
        const newConfig: AxiosRequestConfig = {
            headers: {
                'Access-Token': accessToken.value ? String(accessToken.value) : null
            }
        }
        return newConfig
    }
}

const updateAuthStatus = async <T>(response: AxiosResponse<T>) => {
    const sourceResponse = response.data as ResponseData
    const accessToken = process.client ? useState('accessToken') : ref(null)
    const refreshToken = process.client ? useState('refreshToken') : ref(null)
    const authUser = process.client ? useState('authUser') : ref(null)
    const authUserLogoutMsg = process.client ? useState('authUserLogoutMsg') : ref(null)
    if (sourceResponse.code === 40302) {
        accessToken.value = null
        refreshToken.value = null
        authUser.value = null
        authUserLogoutMsg.value = new Date().getTime()
        authLogout()
        throw new Error('登录凭证已过期')
    }
    if (sourceResponse.code === 40301) {
        accessToken.value = null
        refreshToken.value = null
        authUser.value = null
        authUserLogoutMsg.value = new Date().getTime()
        authLogout()
        throw new Error('用户未登录')
    }
}

/**
 * 检查首次请求返回结果是否为凭证已到期
 * 若已到期则先根据RefreshToken刷新AccessToken后再次发送请求
 */
const checkResponse = async <T>(response: AxiosResponse<T>) => {
    return new Promise(async (resolve, reject) => {
        const sourceResponse = response.data as ResponseData
        if (sourceResponse.code === 40302) {
            const accessToken = process.client ? useState('accessToken') : ref(null)
            const refreshToken = process.client ? useState('refreshToken') : ref(null)
            const o3AccessToken = process.client ? useCookie('o3AccessToken') : ref(null)
            const o3RefreshToken = process.client ? useCookie('o3RefreshToken') : ref(null)

            const res = await postApiCore('/user/refreshToken', {
                refreshToken: refreshToken.value as string
            }) as ResponseData

            if (res.code === 0 && res.data) {
                accessToken.value = res.data.accessToken
                refreshToken.value = res.data.refreshToken
                o3AccessToken.value = res.data.accessToken
                o3RefreshToken.value = res.data.refreshToken
                resolve(true)
            }

            resolve(false)
        } else {
            resolve(false)
        }
    })
}

const getApi = async <T>(url: string, data?: QueryParamsType, config?: AxiosRequestConfig): Promise<ResponseData> => {
    config = await configRequest(config)
    const sourceResponse = await axiosInstance.get<ResponseData>(objectToGetParams(url, data), config)
    const retry = await checkResponse(sourceResponse)
    if (retry) {
        config = await configRequest(config)
        const response = await axiosInstance.get<ResponseData>(objectToGetParams(url, data), config)
        return response.data
    } else {
        await updateAuthStatus(sourceResponse)
        return sourceResponse.data
    }
}

const requestQueue: Array<{
    url: string;
    data?: QueryParamsType;
    config?: AxiosRequestConfig;
    resolve?: (value?: ResponseData | PromiseLike<ResponseData>) => void;
    reject?: (reason?: any) => void;
}> = []

let isExecuting = false

const executeNextRequest = async () => {
    if (requestQueue.length > 0) {
        const { url, data, config, resolve, reject } = requestQueue.shift()
        try {
            const response = await postApiCore(url, data, config)
            resolve(response)
        } catch (error) {
            reject(error)
        }
        executeNextRequest()
    } else {
        isExecuting = false
    }
}

const postApiQueue = async <T>(url: string, data?: QueryParamsType, config?: AxiosRequestConfig): Promise<ResponseData> => {
    return new Promise((resolve, reject) => {
        requestQueue.push({
            url,
            data,
            config,
            resolve,
            reject
        })
        if (!isExecuting) {
            isExecuting = true
            executeNextRequest()
        }
    })
}

const postApi = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<ResponseData> => {
    return postApiCore(url, data, config)
    // config = await configRequest(config)
    // const sourceResponse = await axiosInstance.post<ResponseData>(url, data, config)
    // const retry = await checkResponse(sourceResponse)
    // if (retry) {
    //     config = await configRequest(config)
    //     const response = await axiosInstance.post<ResponseData>(url, data, config)
    //     return response.data
    // } else {
    //     await updateAuthStatus(sourceResponse)
    //     return sourceResponse.data
    // }
}

const postApiCore = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<ResponseData> => {
    config = await configRequest(config)
    const sourceResponse = await axiosInstance.post<ResponseData>(url, data, config)
    const retry = await checkResponse(sourceResponse)
    if (retry) {
        config = await configRequest(config)
        const response = await axiosInstance.post<ResponseData>(url, data, config)
        return response.data
    } else {
        await updateAuthStatus(sourceResponse)
        return sourceResponse.data
    }
}

const putApi = async <T>(url: string, data: QueryParamsType, config?: AxiosRequestConfig): Promise<T> => {
    config = await configRequest(config)
    const sourceResponse = await axiosInstance.put<T>(url, data, config)
    const retry = await checkResponse(sourceResponse)
    if (retry) {
        config = await configRequest(config)
        const response = await axiosInstance.put<T>(url, data, config)
        return response.data
    } else {
        await updateAuthStatus(sourceResponse)
        return sourceResponse.data
    }
}

const delApi = async <T>(url: string, data?: QueryParamsType, config?: AxiosRequestConfig): Promise<T> => {
    config = await configRequest(config)
    const sourceResponse = await axiosInstance.delete<T>(objectToGetParams(url, data), config)
    const retry = await checkResponse(sourceResponse)
    if (retry) {
        config = await configRequest(config)
        const response = await axiosInstance.delete<T>(objectToGetParams(url, data), config)
        return response.data
    } else {
        await updateAuthStatus(sourceResponse)
        return sourceResponse.data
    }
}

const getFile = async <T>(url: string, timeout?: number, onUploadProgress?: (loaded: number, total: number) => void): Promise<T> => {
    if (!timeout) {
        timeout = 30 * 60 * 1000
    }

    const response = await axiosInstance.get(url, {
        timeout,
        headers: {
            'Access-Control-Allow-Origin': '*'
        },
        responseType: 'arraybuffer',
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onUploadProgress) {
                if (progressEvent.total) {
                    onUploadProgress(progressEvent.loaded, progressEvent.total)
                }
            }
        }
    })
    // console.log('getFile done')
    return response.data
}

const putFile = async <T>(url: string, data: unknown, contentType: string, timeout?: number, onUploadProgress?: (loaded: number, total: number) => void): Promise<T> => {
    if (!timeout) {
        timeout = 30 * 60 * 1000
    }

    const response = await axiosInstance.put(url, data, {
        timeout,
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': contentType
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onUploadProgress) {
                if (progressEvent.total) {
                    onUploadProgress(progressEvent.loaded, progressEvent.total)
                }
            }
        }
    })
    // console.log('putFile')
    // console.log(response)
    return response.data
}

export {getApi, postApiQueue, postApi, postApiCore, putApi, delApi, getFile, putFile}
